import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Sparkles, FileText, Copy, Plus, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useDraftStorage } from "@/hooks/useDraftStorage";
import { sanitizeText, sanitizeEmail, sanitizePhone } from "@/lib/security/sanitize";
import { PdfDownloadButton } from "@/components/PdfDownloadButton";

interface WorkExperience {
  id: string;
  jobTitle: string;
  company: string;
  dateFrom: string;
  dateTo: string;
  responsibilities: string;
}

interface ResumeData {
  personalInfo: {
    name: string;
    email: string;
    phone: string;
    location: string;
  };
  workExperiences: WorkExperience[];
  skills: string[];
  education: Array<{
    qualification: string;
    institution: string;
    year: string;
    type: string;
  }>;
  additional: {
    volunteer: string;
    projects: string;
    interests: string;
  };
  hasCriminalRecord: boolean;
}

export default function AIResumeBuilder() {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState<string | null>(null);
  const [template, setTemplate] = useState("modern");
  const [generatedResume, setGeneratedResume] = useState("");
  const [currentSkill, setCurrentSkill] = useState("");

  // Use secure database storage with auto-save
  const {
    data: savedResume,
    setData: setSavedResume,
    isLoading: isDraftLoading,
    isSaving: isDraftSaving,
  } = useDraftStorage<ResumeData>({
    draftType: 'resume',
    autoSaveInterval: 30000, // 30 seconds
    expiresInDays: 7,
  });

  const [resumeData, setResumeData] = useState<ResumeData>({
    personalInfo: {
      name: "",
      email: "",
      phone: "",
      location: "",
    },
    workExperiences: [],
    skills: [],
    education: [],
    additional: {
      volunteer: "",
      projects: "",
      interests: "",
    },
    hasCriminalRecord: false,
  });

  // Load draft on mount
  useEffect(() => {
    if (savedResume) {
      setResumeData(savedResume);
    }
  }, [savedResume]);

  // Sync resume changes to draft storage with sanitization
  useEffect(() => {
    if (Object.values(resumeData.personalInfo).some(v => v) || 
        resumeData.workExperiences.length > 0 ||
        resumeData.skills.length > 0) {
      const sanitizedData = {
        ...resumeData,
        personalInfo: {
          name: sanitizeText(resumeData.personalInfo.name),
          email: sanitizeEmail(resumeData.personalInfo.email),
          phone: sanitizePhone(resumeData.personalInfo.phone),
          location: sanitizeText(resumeData.personalInfo.location),
        },
      };
      setSavedResume(sanitizedData);
    }
  }, [resumeData, setSavedResume]);

  const addWorkExperience = () => {
    setResumeData({
      ...resumeData,
      workExperiences: [
        ...resumeData.workExperiences,
        {
          id: Date.now().toString(),
          jobTitle: "",
          company: "",
          dateFrom: "",
          dateTo: "",
          responsibilities: "",
        },
      ],
    });
  };

  const updateWorkExperience = (id: string, field: string, value: string) => {
    setResumeData({
      ...resumeData,
      workExperiences: resumeData.workExperiences.map((exp) =>
        exp.id === id ? { ...exp, [field]: value } : exp
      ),
    });
  };

  const removeWorkExperience = (id: string) => {
    setResumeData({
      ...resumeData,
      workExperiences: resumeData.workExperiences.filter((exp) => exp.id !== id),
    });
  };

  const addSkill = () => {
    if (currentSkill.trim() && !resumeData.skills.includes(currentSkill.trim())) {
      setResumeData({
        ...resumeData,
        skills: [...resumeData.skills, currentSkill.trim()],
      });
      setCurrentSkill("");
    }
  };

  const removeSkill = (skill: string) => {
    setResumeData({
      ...resumeData,
      skills: resumeData.skills.filter((s) => s !== skill),
    });
  };

  const enhanceExperience = async (id: string) => {
    const experience = resumeData.workExperiences.find((exp) => exp.id === id);
    if (!experience || !experience.responsibilities) return;

    setIsEnhancing(id);
    try {
      const { data, error } = await supabase.functions.invoke("resume-assistant", {
        body: {
          stepType: "enhance-bullet",
          userData: {
            text: experience.responsibilities,
          },
        },
      });

      if (error) throw error;

      // Handle both plain text and JSON responses
      const enhancedText = typeof data.result === 'string' ? data.result : data.result.result;
      updateWorkExperience(id, "responsibilities", enhancedText);
      toast({
        title: "Experience enhanced!",
        description: "Your work experience has been improved with AI.",
      });
    } catch (error) {
      console.error("Enhancement error:", error);
      toast({
        title: "Enhancement failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsEnhancing(null);
    }
  };

  const generateResume = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("resume-assistant", {
        body: {
          stepType: "summary",
          userData: {
            experiences: resumeData.workExperiences,
            skills: resumeData.skills,
            education: resumeData.education,
            hasCriminalRecord: resumeData.hasCriminalRecord,
            personalInfo: resumeData.personalInfo,
          },
        },
      });

      if (error) throw error;

      // Handle both plain text and JSON responses
      const resumeText = typeof data.result === 'string' ? data.result : data.result.result;
      setGeneratedResume(resumeText);
      toast({
        title: "Resume created!",
        description: "Download or copy below.",
      });
    } catch (error) {
      console.error("Generation error:", error);
      toast({
        title: "Generation failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedResume || getPreviewText());
    toast({
      title: "Copied!",
      description: "Resume copied to clipboard.",
    });
  };


  const getPreviewText = () => {
    if (generatedResume) return generatedResume;

    let preview = "";
    
    if (resumeData.personalInfo.name) {
      preview += `${resumeData.personalInfo.name}\n`;
      if (resumeData.personalInfo.email) preview += `${resumeData.personalInfo.email}`;
      if (resumeData.personalInfo.phone) preview += ` | ${resumeData.personalInfo.phone}`;
      if (resumeData.personalInfo.location) preview += ` | ${resumeData.personalInfo.location}`;
      preview += "\n\n";
    }

    if (resumeData.skills.length > 0) {
      preview += "SKILLS\n";
      preview += resumeData.skills.join(" • ") + "\n\n";
    }

    if (resumeData.workExperiences.length > 0) {
      preview += "EXPERIENCE\n";
      resumeData.workExperiences.forEach((exp) => {
        if (exp.jobTitle) preview += `${exp.jobTitle}`;
        if (exp.company) preview += ` | ${exp.company}`;
        if (exp.dateFrom) preview += ` | ${exp.dateFrom} - ${exp.dateTo || "Present"}`;
        preview += "\n";
        if (exp.responsibilities) preview += exp.responsibilities + "\n";
        preview += "\n";
      });
    }

    if (resumeData.education.length > 0) {
      preview += "EDUCATION\n";
      resumeData.education.forEach((edu) => {
        if (edu.qualification) preview += `${edu.qualification}`;
        if (edu.institution) preview += ` | ${edu.institution}`;
        if (edu.year) preview += ` | ${edu.year}`;
        preview += "\n";
      });
    }

    return preview || "Start filling in your information to see a preview...";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            AI-Powered Resume Builder
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Transform your life experiences into professional qualifications with intelligent guidance and ROA-compliant criminal history support
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Input Form */}
          <div className="space-y-4">
            <Accordion type="single" collapsible className="space-y-4">
              {/* Personal Information */}
              <AccordionItem value="personal" className="bg-white rounded-lg shadow-lg border-0">
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    <span className="font-semibold">Personal Information</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={resumeData.personalInfo.name}
                      onChange={(e) =>
                        setResumeData({
                          ...resumeData,
                          personalInfo: { ...resumeData.personalInfo, name: e.target.value },
                        })
                      }
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={resumeData.personalInfo.email}
                      onChange={(e) =>
                        setResumeData({
                          ...resumeData,
                          personalInfo: { ...resumeData.personalInfo, email: e.target.value },
                        })
                      }
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={resumeData.personalInfo.phone}
                      onChange={(e) =>
                        setResumeData({
                          ...resumeData,
                          personalInfo: { ...resumeData.personalInfo, phone: e.target.value },
                        })
                      }
                      placeholder="07123 456789"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location (City)</Label>
                    <Input
                      id="location"
                      value={resumeData.personalInfo.location}
                      onChange={(e) =>
                        setResumeData({
                          ...resumeData,
                          personalInfo: { ...resumeData.personalInfo, location: e.target.value },
                        })
                      }
                      placeholder="London"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Work Experience */}
              <AccordionItem value="experience" className="bg-white rounded-lg shadow-lg border-0">
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    <span className="font-semibold">Work Experience</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 space-y-6">
                  {resumeData.workExperiences.map((exp, index) => (
                    <Card key={exp.id} className="relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2"
                        onClick={() => removeWorkExperience(exp.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <CardContent className="pt-6 space-y-4">
                        <div>
                          <Label>Job Title</Label>
                          <Input
                            value={exp.jobTitle}
                            onChange={(e) => updateWorkExperience(exp.id, "jobTitle", e.target.value)}
                            placeholder="Software Developer"
                          />
                        </div>
                        <div>
                          <Label>Company</Label>
                          <Input
                            value={exp.company}
                            onChange={(e) => updateWorkExperience(exp.id, "company", e.target.value)}
                            placeholder="Tech Company Ltd"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>From</Label>
                            <Input
                              type="month"
                              value={exp.dateFrom}
                              onChange={(e) => updateWorkExperience(exp.id, "dateFrom", e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>To</Label>
                            <Input
                              type="month"
                              value={exp.dateTo}
                              onChange={(e) => updateWorkExperience(exp.id, "dateTo", e.target.value)}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Responsibilities</Label>
                          <Textarea
                            value={exp.responsibilities}
                            onChange={(e) => updateWorkExperience(exp.id, "responsibilities", e.target.value)}
                            placeholder="Describe your key responsibilities and achievements..."
                            rows={4}
                          />
                        </div>
                        <Button
                          onClick={() => enhanceExperience(exp.id)}
                          disabled={isEnhancing === exp.id || !exp.responsibilities}
                          className="w-full"
                          variant="outline"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          {isEnhancing === exp.id ? "Enhancing..." : "Enhance with AI"}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                  <Button onClick={addWorkExperience} variant="outline" className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Job
                  </Button>
                </AccordionContent>
              </AccordionItem>

              {/* Skills */}
              <AccordionItem value="skills" className="bg-white rounded-lg shadow-lg border-0">
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                    <span className="font-semibold">Skills</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 space-y-4">
                  <div>
                    <Label>Add Skills</Label>
                    <div className="flex gap-2">
                      <Input
                        value={currentSkill}
                        onChange={(e) => setCurrentSkill(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())}
                        placeholder="Type a skill and press Enter"
                      />
                      <Button onClick={addSkill} size="icon">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {resumeData.skills.map((skill) => (
                      <div
                        key={skill}
                        className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full flex items-center gap-2"
                      >
                        <span>{skill}</span>
                        <button
                          onClick={() => removeSkill(skill)}
                          className="hover:text-indigo-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Privacy Options */}
              <AccordionItem value="privacy" className="bg-white rounded-lg shadow-lg border-0">
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    <span className="font-semibold">Privacy Options</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="criminal-record"
                      checked={resumeData.hasCriminalRecord}
                      onCheckedChange={(checked) =>
                        setResumeData({ ...resumeData, hasCriminalRecord: checked as boolean })
                      }
                    />
                    <div className="space-y-2">
                      <Label htmlFor="criminal-record" className="cursor-pointer">
                        I have a criminal record and need ROA-compliant guidance
                      </Label>
                      {resumeData.hasCriminalRecord && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                          <p className="font-medium mb-1">Your privacy is protected</p>
                          <p>
                            Your resume will focus on skills and rehabilitation. You don't need to
                            disclose spent convictions under the Rehabilitation of Offenders Act.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="space-y-3">
              <Button
                onClick={generateResume}
                disabled={isGenerating || !resumeData.personalInfo.name || !resumeData.personalInfo.email}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-lg h-12"
              >
                {isGenerating ? "Creating your professional resume..." : "Generate Resume"}
              </Button>
              <Button
                onClick={async () => {
                  // Sanitize data before saving
                  const sanitizedData = {
                    ...resumeData,
                    personalInfo: {
                      name: sanitizeText(resumeData.personalInfo.name),
                      email: sanitizeEmail(resumeData.personalInfo.email),
                      phone: sanitizePhone(resumeData.personalInfo.phone),
                      location: sanitizeText(resumeData.personalInfo.location),
                    },
                  };
                  await setSavedResume(sanitizedData);
                  toast({
                    title: "Draft saved",
                    description: "Your resume draft has been saved securely to the database",
                  });
                }}
                variant="secondary"
                className="w-full"
              >
                Save Draft
              </Button>
            </div>
          </div>

          {/* Right Column - Live Preview */}
          <div className="lg:sticky lg:top-4 h-fit">
            <Card className="shadow-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Resume Preview</CardTitle>
                  <CardDescription>Live preview of your resume</CardDescription>
                </div>
                <Select value={template} onValueChange={setTemplate}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modern">Modern</SelectItem>
                    <SelectItem value="traditional">Traditional</SelectItem>
                    <SelectItem value="ats">ATS</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                <div id="resume-preview" className="bg-gray-50 rounded-lg p-6 min-h-[500px] whitespace-pre-wrap font-mono text-sm">
                  {getPreviewText()}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <PdfDownloadButton
                    contentId="resume-preview"
                    filename="my-resume"
                    ariaLabel="Download your resume as a PDF document"
                    variant="default"
                    size="default"
                    className="flex-1"
                  />
                  <Button onClick={copyToClipboard} variant="secondary" className="flex-1">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Text
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {isGenerating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-8 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-lg font-medium">Creating your professional resume...</p>
          </Card>
        </div>
      )}
    </div>
  );
}
